import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { LOGO_WHITE } from "../assets/brand";

const CONTACT_EMAIL = "info@sojlondon.org";
const LAST_UPDATED = "June 2026";

const Section = ({ title, children }) => (
  <section className="mb-8" data-testid="privacy-section">
    <h2 className="font-head text-xl font-bold mb-3" style={{ color: "var(--sj-text)" }}>{title}</h2>
    <div className="space-y-3 text-[15px] leading-relaxed" style={{ color: "var(--sj-text-2)" }}>{children}</div>
  </section>
);

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen" style={{ background: "var(--sj-bg)" }} data-testid="privacy-policy-page">
      <header className="text-white" style={{ background: "linear-gradient(135deg, #0B5E4E, #10302B)" }}>
        <div className="max-w-3xl mx-auto px-6 py-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-11 w-11 rounded-xl flex items-center justify-center p-1.5" style={{ background: "rgba(255,255,255,0.1)" }}>
              <img src={LOGO_WHITE} alt="SOJ" className="h-full w-full object-contain" />
            </div>
            <div className="leading-tight">
              <div className="font-head font-extrabold text-[15px]">Streams of Joy</div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-white/70">Erith London</div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-white/80 text-sm mb-2"><ShieldCheck className="h-4 w-4" /> UK GDPR &amp; Data Protection Act 2018</div>
          <h1 className="font-head text-3xl sm:text-4xl font-extrabold">Privacy &amp; Data Protection Policy</h1>
          <p className="text-white/75 mt-2 text-sm">Last updated: {LAST_UPDATED}</p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <Link to="/login" className="inline-flex items-center gap-2 text-sm font-semibold mb-8 hover:underline" style={{ color: "var(--sj-primary)" }} data-testid="privacy-back-link">
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>

        <Section title="1. Who we are">
          <p>Streams of Joy International Erith London ("the Church", "we", "us", "our") is the data controller responsible for your personal data. This policy explains how we collect, use, store and protect your information, and your rights under the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.</p>
          <p>If you have any questions about this policy or how we handle your data, please contact us at <a href={`mailto:${CONTACT_EMAIL}`} className="font-semibold hover:underline" style={{ color: "var(--sj-primary)" }}>{CONTACT_EMAIL}</a>.</p>
        </Section>

        <Section title="2. The information we collect">
          <p>We collect and process the following categories of personal data:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Identity &amp; contact details</strong> — name, email address, phone number, postal address and postcode, date of birth.</li>
            <li><strong>Membership information</strong> — date you joined, the teams/ministries you belong to, your role, and attendance records.</li>
            <li><strong>Pastoral &amp; engagement data</strong> — prayer requests, follow-up and pastoral care notes, and messages exchanged with the Church.</li>
            <li><strong>Giving &amp; donations</strong> — records of tithes, offerings and donations (processed securely via our payment provider; we do not store full card details).</li>
            <li><strong>Media</strong> — profile photos and, where relevant, images from Church events.</li>
            <li><strong>Technical data</strong> — login timestamps and information necessary to keep your account secure.</li>
          </ul>
        </Section>

        <Section title="3. Special category data (religious beliefs)">
          <p>Because we are a church, the fact that you are a member may reveal information about your religious beliefs. This is "special category data" under UK GDPR. We process it on the basis of your consent and/or Article 9(2)(d), which permits a not-for-profit body with a religious aim to process the data of its members and people in regular contact with it, provided the data is not shared outside the Church without your consent.</p>
        </Section>

        <Section title="4. How and why we use your data (lawful bases)">
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Consent</strong> — to send you communications by SMS and email, and to include you in Church activities.</li>
            <li><strong>Legitimate interests</strong> — to administer membership, track attendance, provide pastoral care, and run the life of the Church effectively.</li>
            <li><strong>Legal obligation</strong> — to keep financial and Gift Aid records where required by law.</li>
            <li><strong>Contract / vital interests</strong> — where necessary to provide a service you have requested or to protect someone's safety.</li>
          </ul>
        </Section>

        <Section title="5. Communications">
          <p>We use your contact details to send you Church announcements, pastoral messages, event information and administrative notices. You can opt out of non-essential communications at any time by contacting us or using the unsubscribe option where provided. SMS messages include a "do not reply" notice.</p>
        </Section>

        <Section title="6. Who we share your data with">
          <p>We do not sell your personal data. We share it only with trusted service providers ("data processors") who help us operate, under contracts that protect your data:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li><strong>Twilio</strong> — to deliver SMS text messages.</li>
            <li><strong>Resend</strong> — to deliver email messages.</li>
            <li><strong>Stripe</strong> — to process online giving and donations securely.</li>
          </ul>
          <p>We may also disclose data where required by law or to protect the rights and safety of individuals.</p>
        </Section>

        <Section title="7. International transfers">
          <p>Some of our providers may process data outside the UK. Where this happens, we ensure appropriate safeguards are in place (such as UK adequacy regulations or International Data Transfer Agreements) so your data remains protected to UK standards.</p>
        </Section>

        <Section title="8. How long we keep your data">
          <p>We keep your personal data only for as long as necessary for the purposes described above, or as required by law (for example, Gift Aid and financial records are retained for at least 6 years). Membership data is kept while you remain in contact with the Church and for a reasonable period afterwards, after which it is securely deleted or anonymised.</p>
        </Section>

        <Section title="9. How we keep your data secure">
          <p>Access to personal data is restricted to authorised leaders and staff on a role-based basis. Accounts are password protected, sensitive actions are recorded in an audit log, and data is transmitted over encrypted connections.</p>
        </Section>

        <Section title="10. Your rights">
          <p>Under UK GDPR you have the right to:</p>
          <ul className="list-disc pl-6 space-y-1.5">
            <li>Be informed about how your data is used;</li>
            <li>Access a copy of the data we hold about you;</li>
            <li>Request correction of inaccurate or incomplete data;</li>
            <li>Request erasure of your data ("right to be forgotten");</li>
            <li>Restrict or object to processing;</li>
            <li>Data portability;</li>
            <li>Withdraw consent at any time.</li>
          </ul>
          <p>To exercise any of these rights, contact us at <a href={`mailto:${CONTACT_EMAIL}`} className="font-semibold hover:underline" style={{ color: "var(--sj-primary)" }}>{CONTACT_EMAIL}</a>. We will respond within one month.</p>
        </Section>

        <Section title="11. Cookies">
          <p>This application uses only essential cookies and local storage needed to keep you signed in and to operate securely. We do not use advertising or third-party tracking cookies.</p>
        </Section>

        <Section title="12. Complaints">
          <p>If you are unhappy with how we have handled your data, please contact us first so we can put things right. You also have the right to complain to the UK's supervisory authority, the Information Commissioner's Office (ICO), at <a href="https://ico.org.uk/" target="_blank" rel="noreferrer" className="font-semibold hover:underline" style={{ color: "var(--sj-primary)" }}>ico.org.uk</a> or by calling 0303 123 1113.</p>
        </Section>

        <Section title="13. Changes to this policy">
          <p>We may update this policy from time to time. Any changes will be posted on this page with a revised "last updated" date.</p>
        </Section>

        <div className="mt-10 pt-6 border-t text-sm" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-3)" }}>
          <p>Streams of Joy International Erith London · Church Management System</p>
        </div>
      </main>
    </div>
  );
}
